import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import HeadlessBtn from '../../../shared/component/headless/button/HeadlessBtn';
import { BlackFullWidthBtn } from '../../../shared/component/headless/button/BtnBody';
import { WorkViewerInfo } from '../../../shared/component/headless/input/InputBody';
import { NormalSmallTextArea } from '../../../shared/component/headless/textarea/TextAreaBody';
import MoleculeInputDivForModal from '../../../shared/component/molecule/MoleculeInputDivForModal';
import MoleculeTextareaDescriptionForModal from '../../../shared/component/molecule/MoleculeTextareaDescriptionForModal';
import { useModalStore } from '../../../shared/store/portal/modalStore';
import { getBtnName } from '../../../shared/constants/btnNames';
import { useSetting } from '../../../shared/hooks/useApi/useSetting';
import { TextAlignment } from '../../../shared/enum/EnumRepository';
import { SEO_DESCRIPTION_MAX_LENGTH, SEO_TITLE_MAX_LENGTH } from '../../../shared/constants/limits';
import UniversalIconBtn from '../../../shared/component/headless/button/UniversalIconBtn';
import belowIcon from '../../../asset/icon/below.png';
import { getModalMessage } from '../../../shared/constants/modalMessages';

interface ModalProps {
  setIsUserInput: (isUserInput: boolean) => void;
}

const ChangeSearchEngineModalMobile: React.FC<ModalProps> = ({ setIsUserInput }) => {
  const { standardModal, clearModal } = useModalStore();
  const { setting, updateSeo } = useSetting();
  const [name, setName] = useState(setting?.seo.name ?? '');
  const [nameEn, setNameEn] = useState(setting?.seo.nameEn ?? '');
  const [description, setDescription] = useState(setting?.seo.description ?? '');
  const [descriptionEn, setDescriptionEn] = useState(setting?.seo.descriptionEn ?? '');

  useEffect(() => {
    if (!setting) {
      setIsUserInput(false);
      return;
    }

    setIsUserInput(
      name !== (setting.seo.name ?? '') ||
      nameEn !== (setting.seo.nameEn ?? '') ||
      description !== setting.seo.description ||
      descriptionEn !== (setting.seo.descriptionEn ?? '')
    );
  }, [description, descriptionEn, nameEn, name, setIsUserInput, setting]);

  if (standardModal == null || setting == null) return null;

  const handleChange = async () => {
    await updateSeo({
      subTitle: setting.seo.subTitle,
      name,
      nameEn,
      description,
      descriptionEn,
      updateUploadFileReq: {
        uploadFileId: setting.seo.uploadFile.id,
        originUrl: setting.seo.uploadFile.originUrl,
        allowDownload: setting.seo.uploadFile.allowDownload,
        width: setting.seo.uploadFile.width,
        height: setting.seo.uploadFile.height,
      },
    });
  };

  return (
    <ModalBody>
      <TitleWrapper>
        <Title>{standardModal.title}</Title>
        <UniversalIconBtn
          size="fit"
          border={false}
          isTransparent
          backgroundImg={belowIcon}
          onClick={() => clearModal(400)}
        />
      </TitleWrapper>
      <OptionContainer>
        <InfoRow>
          <InfoLabel>Name</InfoLabel>
          <MoleculeInputDivForModal
            value={name}
            placeholder="Name"
            handleChange={(e) => setName(e.target.value)}
            inputStyle={WorkViewerInfo}
            alignment={TextAlignment.LEFT}
            maxLength={SEO_TITLE_MAX_LENGTH}
          />
        </InfoRow>
        <InfoRow>
          <InfoLabel>Name(EN)</InfoLabel>
          <MoleculeInputDivForModal
            value={nameEn}
            placeholder="Name(EN)"
            handleChange={(e) => setNameEn(e.target.value)}
            inputStyle={WorkViewerInfo}
            alignment={TextAlignment.LEFT}
            maxLength={SEO_TITLE_MAX_LENGTH}
          />
        </InfoRow>
        <InfoRow>
          <InfoLabel>Description</InfoLabel>
          <MoleculeTextareaDescriptionForModal
            value={description}
            placeholder={getModalMessage('SEO_DESCRIPTION_HINT')}
            handleChange={(e) => setDescription(e.target.value)}
            StyledTextarea={SeoDescriptionTextarea}
            alignment={TextAlignment.LEFT}
            maxLength={SEO_DESCRIPTION_MAX_LENGTH}
            maxRows={6}
          />
        </InfoRow>
        <InfoRow>
          <InfoLabel>Description(EN)</InfoLabel>
          <MoleculeTextareaDescriptionForModal
            value={descriptionEn}
            placeholder={getModalMessage('SEO_DESCRIPTION_EN_HINT')}
            handleChange={(e) => setDescriptionEn(e.target.value)}
            StyledTextarea={SeoDescriptionTextarea}
            alignment={TextAlignment.LEFT}
            maxLength={SEO_DESCRIPTION_MAX_LENGTH}
            maxRows={6}
          />
        </InfoRow>
      </OptionContainer>
      <HeadlessBtn value={getBtnName('CHANGE')} handleClick={handleChange} StyledBtn={SubmitButton} />
    </ModalBody>
  );
};

const ModalBody = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const TitleWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  color: ${({ theme }) => theme.colors.color_Gray_02};
  margin-top: clamp(12px, 2vh, 30px);
  margin-bottom: clamp(20px, 8vh, 30px);
`;

const Title = styled.h2`
  ${({ theme }) => theme.typography.Body_01_1};
  color: ${({ theme }) => theme.colors.color_Gray_02};
`;



const OptionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  color: ${({ theme }) => theme.colors.color_Gray_03};
`;

const InfoRow = styled.div`
  display: flex;
  gap: 8px;
  ${({ theme }) => theme.typography.Body_04};
`;

const InfoLabel = styled.span`
  min-width: 100px;
  padding: 4px 0;
  color: ${({ theme }) => theme.colors.color_Gray_03};
  font-weight: 500;
`;

const SeoDescriptionTextarea = styled(NormalSmallTextArea)`
  min-height: 60px;
  margin-top: 4px;
  ${({ theme }) => theme.typography.Body_04};
`;

const SubmitButton = styled(BlackFullWidthBtn)`
  margin: 20px 0px 15px 0px;
`;

export default ChangeSearchEngineModalMobile;
